import React, { useState, memo, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { setActivityIndex } from '@/lib/slices/activity-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import SubunitTable from './SubunitTable';
import ProgressCircle from '@/common/components/ProgressCircle';
import { DeleteItemIcon } from '@/assets/icons/button';
import { ChevronRightIcon } from '@/assets/icons/chevron';
import { DraggableDotsVerticalIcon } from '@/assets/ui/dots';
import { CompletionBadge, AddContentButton, InlineEditableText } from '@/common/components/course';

import ProgressUtilityService from '@/services/course/progress-utility-service';
import CourseUtilityService from '@/services/course/course-utility-service';
import CourseUpdateService from '@/services/course/course-update-service';
import { useInlineEdit } from '@/common/hooks/useInlineEdit';
import { ActivityData, SubunitData, UnitData, UnitId } from 'course-types';

interface UnitCardProps {
  unit: UnitData;
  unitId: string;
  initialExpanded?: boolean;
  onExpandedChange?: (unitId: string, expanded: boolean) => void;
  expandAll?: number;
  collapseAll?: number;
  isAdminView?: boolean;
  isEditable?: boolean;
  onClick?: (
    activity: ActivityData,
    subunit?: SubunitData & { id: string },
    unitId?: string,
  ) => void;
}

function UnitCard({
  unit,
  unitId,
  initialExpanded = false,
  onExpandedChange,
  expandAll,
  collapseAll,
  isAdminView = false,
  isEditable = true,
  onClick,
}: UnitCardProps): React.JSX.Element {
  // Internal state for expansion
  const [expanded, setExpanded] = useState(initialExpanded);

  // Handle expand all / collapse all commands from parent
  useEffect(() => {
    if (expandAll !== undefined && expandAll > 0) {
      setExpanded(true);
      onExpandedChange?.(unitId, true);
    }
  }, [expandAll]);

  useEffect(() => {
    if (collapseAll !== undefined && collapseAll > 0) {
      setExpanded(false);
      onExpandedChange?.(unitId, false);
    }
  }, [collapseAll]);

  // Keep internal state in sync when the parent-chosen initial state changes
  useEffect(() => {
    setExpanded(initialExpanded);
  }, [initialExpanded]);

  // Handle toggle expansion
  const handleToggleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onExpandedChange?.(unitId, newExpanded);
  };

  // Compute real completion status for this unit using progress data
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const activeCourse = useAppSelector(() => store.getState().course.activeCourseData);

  // Disable delete when only one unit exists
  const isOnlyUnit = Object.keys(activeCourse?.units ?? {}).length < 2;

  // Get fresh unit data from props first (has complete prerequisite data), fallback to Redux
  const freshUnitData = unit || activeCourse?.units?.[unitId];

  // Drag and drop functionality for units
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: unitId,
    disabled: !(isAdminView && isEditable),
  });

  const totalActivities = ProgressUtilityService.getUnitTrackableTotal(freshUnitData);
  const completedActivities = ProgressUtilityService.getUnitCompletedActivities(
    activeCourse.id,
    unitId as UnitId,
    freshUnitData,
  );
  const isCompleted = completedActivities === totalActivities;
  const progressPercentage =
    totalActivities === 0 ? 100 : Math.round((completedActivities / totalActivities) * 100);

  // Calculate total number of activities in the unit
  const totalUnitActivities = CourseUtilityService.calculateUnitActivities(freshUnitData);
  const unitResources = CourseUtilityService.calculateUnitResources(freshUnitData);

  // Use inline edit hook for unit name editing
  const inlineEdit = useInlineEdit({
    initialValue: freshUnitData.name,
    onSave: async (newName) => {
      const result = await CourseUpdateService.updateUnit(unitId, newName, unit.description || '');
      if (result.success) {
        const updatedCourse = CourseUpdateService.buildUpdatedCourseWithUnitName(
          activeCourse,
          unitId,
          newName,
        );
        dispatch(setActiveCourseData(updatedCourse));
      }
    },
  });

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    inlineEdit.startEditing(freshUnitData.name);
  };

  // Open modal for adding new content/activity to unit (not within a subunit)
  const onAddUnitContent = () => {
    dispatch(setSubunitId(undefined)); // No subunit for unit-level activities
    dispatch(setUnitId(unitId));

    // Sets index to length of current activity array + number of subunits => should be pushed on to end
    const unitActivitiesLength = (freshUnitData.activities ?? []).length;
    const subunitsLength = Object.keys(freshUnitData.subunits ?? {}).length;
    dispatch(setActivityIndex(unitActivitiesLength + subunitsLength));

    dispatch(setModalVisible(true));
    dispatch(setModalName('AddNewContent'));
  };

  // Open modal for adding new subunit to unit
  const onAddSubunit = () => {
    dispatch(setUnitId(unitId));
    dispatch(setModalVisible(true));
    dispatch(setModalName('AddNewSubunit'));
  };

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      className='flex flex-col bg-surface_white items-start gap-0 self-stretch rounded-3xl border border-border_gray_200 shadow-[0_12px_16px_-4px_rgba(10,13,18,0.08),0_4px_6px_-2px_rgba(10,13,18,0.03)] p-0'
    >
      {/* Extra padding on mobile so content isn't cramped */}
      <div
        onClick={handleToggleExpand}
        className={`w-full cursor-pointer px-4 md:px-4 px-6 pt-6 md:pt-4 hover:bg-surface_gray_50 transition-colors rounded-t-3xl ${expanded ? 'rounded-t-3xl pb-6 border-b border-border_gray_200' : 'rounded-3xl pb-6 md:pb-4'}`}
      >
        <div className='flex flex-row w-full items-center gap-4 justify-between'>
          <div className='flex flex-row items-center gap-2 flex-1'>
            {isAdminView && isEditable && (
              <div
                {...attributes}
                {...listeners}
                className='cursor-move'
                onClick={(e) => e.stopPropagation()}
              >
                <DraggableDotsVerticalIcon className='w-4 h-4 text-text_gray_900' />
              </div>
            )}
            <button className='p-2 rounded-[8px] w-fit h-fit bg-[color-mix(in_srgb,var(--color-brand-highlight)_13.6%,var(--surface-white))] hover:bg-surface_gray_100 transition-colors'>
              <div
                className='flex items-center justify-center w-6 h-6 transition-transform duration-200'
                style={{
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
              >
                <ChevronRightIcon className='w-6 h-6 text-primary' />
              </div>
            </button>

            {/* Mobile fix: Added flex-1 to let title take full width, moved completion badge to same row as activity count. More spacing on mobile for readability */}
            <div className='flex flex-col gap-2 md:gap-1 items-start flex-1'>
              {isAdminView && isEditable ? (
                <InlineEditableText
                  text={freshUnitData.name}
                  isEditing={inlineEdit.isEditing}
                  editValue={inlineEdit.editValue}
                  onEditClick={handleEditClick}
                  onChange={inlineEdit.handleChange}
                  onBlur={inlineEdit.handleSave}
                  onKeyDown={inlineEdit.handleKeyDown}
                  id={unitId ?? 'unitName'}
                  textElement='h3'
                  textClassName='text-text_gray_900 text-lg not-italic font-semibold leading-7'
                />
              ) : (
                <h3 className='text-text_gray_900 text-lg not-italic font-semibold leading-7'>
                  {freshUnitData.name}
                </h3>
              )}
              {/* Completion badge shares row with activity count, extra space on mobile so they're not too close */}
              <div className='flex items-center justify-between w-full gap-3 md:gap-2'>
                <span className='text-text_gray_600 text-xs not-italic font-normal leading-[18px]'>
                  {Object.keys(freshUnitData.subunits || {}).length} subunits •{' '}
                  {CourseUtilityService.calculateUnitActivities(freshUnitData)} activit
                  {CourseUtilityService.calculateUnitActivities(freshUnitData) === 1 ? 'y' : 'ies'}
                  {CourseUtilityService.calculateUnitResources(freshUnitData) > 0
                    ? ` • ${CourseUtilityService.calculateUnitResources(freshUnitData)} ${CourseUtilityService.calculateUnitResources(freshUnitData) === 1 ? 'resource' : 'resources'}`
                    : ''}
                </span>
                {!isAdminView && isCompleted && (
                  <div className='md:hidden'>
                    <CompletionBadge />
                  </div>
                )}
              </div>
            </div>

            {!isAdminView &&
              (isCompleted ? (
                <div className='hidden md:block'>
                  <CompletionBadge />
                </div>
              ) : (
                <div className='flex flex-col gap-2 p-2 items-center justify-center'>
                  <ProgressCircle size={40} progress={progressPercentage} />
                  <span className='text-text_gray_600 text-center text-xs not-italic font-medium leading-[18px]'>
                    Complete
                  </span>
                </div>
              ))}
          </div>
          {isAdminView && isEditable && (
            <button
              onClick={() => {
                if (isOnlyUnit) return;
                dispatch(setUnitId(unitId));
                dispatch(setModalVisible(true));
                dispatch(setModalName('DeleteUnit'));
              }}
              disabled={isOnlyUnit}
              className={`flex items-center justify-center p-2 rounded-[8px] w-fit h-fit group transition-colors ${
                isOnlyUnit ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface_red_100'
              }`}
            >
              <DeleteItemIcon className='h-5 w-5 text-[#A4A7AE] group-hover:text-red-600' />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className='w-full border-t border-border_gray_200 rounded-b-3xl pt-6 pb-4 px-4'>
          <div className='flex flex-col gap-4'>
            <SubunitTable
              subunits={freshUnitData.subunits}
              activities={freshUnitData.activities}
              unitId={unitId}
              unitName={freshUnitData.name}
              onClick={onClick}
              isAdminView={isAdminView}
              isEditable={isEditable}
            />
            {isAdminView && isEditable && (
              <div className='flex gap-3 w-full'>
                <AddContentButton
                  text='Add Subunit'
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddSubunit();
                  }}
                  fullWidth={false}
                  className='px-4'
                />
                <AddContentButton
                  text='Add Unit Content Item'
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddUnitContent();
                  }}
                  fullWidth
                  className='flex-1'
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(UnitCard);
